import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Layout, Menu, Tooltip, Badge } from "antd";
import {
    DashboardOutlined,
    TeamOutlined,
    CalendarOutlined,
    ShopOutlined,
    AuditOutlined,
    IdcardOutlined,
    BankOutlined,
    CreditCardOutlined,
    ReadOutlined,
    CameraOutlined,
} from "@ant-design/icons";
import { localKeys, role } from "@/utils/constants";
import { localData } from "@/services/helper";
import { AuthDataType } from "@/types/authTypes";
import { LayoutDashboard, LayoutTemplate } from "./Icons";
import { usePermissions } from "@/hooks/usePermission";
import { Route } from "lucide-react";

const { Sider } = Layout;

const Sidebar: React.FC<{ collapsed: boolean; onToggle?: () => void }> = ({ collapsed }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { hasPermission } = usePermissions();
    const permissions = useSelector((state: any) => state.accessibleRoutes);
    const routes = useSelector((state: any) => state.getRoutes);
    const skipPermissionCheck = (permissions?.list?.length ?? 0) === 0;
    const auth = useMemo<AuthDataType | null>(
        () => localData.get<AuthDataType>(localKeys.authData),
        [],
    );
    const [pendingCount, setPendingCount] = useState(0);
    const isSuperAdmin = auth?.role === role.SUPER_ADMIN_ROLE;

    const iconStyle = { fontSize: 18 };

    const isPhotographer = auth?.role === role.FREELANCE_PHOTOGRAPHER_ROLE;
    const isStudioAdmin = auth?.role === role.STUDIO_ADMIN_ROLE;

    const iconMap: Record<string, React.ReactNode> = {
        "/dashboard": <LayoutDashboard size={iconStyle} />,
        "/review": <AuditOutlined style={iconStyle} />,
        "/users": <TeamOutlined style={iconStyle} />,
        "/events": <CalendarOutlined style={iconStyle} />,
        "/enquiries": <IdcardOutlined style={iconStyle} />,
        "/studios": <ShopOutlined style={iconStyle} />,
        "/availability": <CalendarOutlined style={iconStyle} />,
        "/album-templates": <LayoutTemplate size={iconStyle} />,
        "/subscription-plans": <CreditCardOutlined style={iconStyle} />,
        "/subscription": <BankOutlined style={iconStyle} />,
        "/routes": <Route size={14} />,
    };

    const menuItems = (routes?.list ?? [])
        .filter((route: any) => {
            if (!route.extra?.includes("sidebar")) return false;
            // Filter by allowedRoles from API
            if (route.isPublic) return false;
            if (route.allowedRoles?.length) {
                const allowed = route.allowedRoles.some((r: any) => r.role === auth?.role);
                if (!allowed) return false;
            }

            if (isSuperAdmin || isPhotographer || skipPermissionCheck) return true;

            return hasPermission(route.path, "view");
        })
        .map((route: any) => {
            const isStudio = route.path === "/studios";
            const key = isStudio && !isSuperAdmin ? "/studios/view" : route.path;
            const label = isStudio && !isSuperAdmin ? "My Studio" : route.label;

            return {
                key,
                icon: iconMap[route.path] ?? <DashboardOutlined style={iconStyle} />,
                label,
            };
        });

    const SIDEBAR_ROUTE_MAP = [
        {
            menu: "/dashboard",
            routes: ["/dashboard"],
        },

        {
            menu: "/users",
            routes: ["/users", "/users/view", "/users/edit", "/users/invite"],
        },

        {
            menu: "/review",
            routes: [
                "/review",
                "/application-review",
                "/admin-review",
                "/referral-decision",
                "/referrals",
                "/admin/approvals",
                "/admin/delete-requests",
            ],
        },

        {
            menu: "/events",
            routes: ["/events", "/events/management"],
        },

        {
            menu: "/enquiries",
            routes: ["/enquiries", "/enquiries/view", "/enquiries/edit", "/enquiries/create"],
        },

        {
            menu: "/studios",
            routes: ["/studios", "/studios/view", "/studios/edit"],
        },

        {
            menu: "/album-templates",
            routes: [
                "/album-templates",
                "/album-templates/create",
                "/album-templates/edit",
                "/album-builder",
            ],
        },
        {
            menu: "/availability",
            routes: ["/availability"],
        },
        {
            menu: "/subscription-plans",
            routes: ["/subscription-plans"],
        },
        {
            menu: "/routes",
            routes: ["/routes"],
        },
    ];

    const selectedKey =
        SIDEBAR_ROUTE_MAP.find((item) =>
            item.routes.some((route) => location.pathname.startsWith(route)),
        )?.menu ?? "";
    const handleOnClick = (key: string) => {
        navigate(key, !isSuperAdmin ? { state: auth?.studioId[0] } : {});
    };
    return (
        <Sider
            width={240}
            collapsedWidth={64}
            trigger={null}
            collapsible
            collapsed={collapsed}
            className="app-sidebar"
        >
            <div className="app-sidebar__logo">
                <div className="app-sidebar__brand">
                    {!collapsed && (
                        <div className="app-sidebar__brand-icon">
                            <CameraOutlined />
                        </div>
                    )}
                    {collapsed ? (
                        <span>
                            P<span className="app-sidebar__brand-highlight">4</span>P
                        </span>
                    ) : (
                        <span>
                            Pics<span className="app-sidebar__brand-highlight">4</span>Pilot
                        </span>
                    )}
                </div>
            </div>
            <div className="app-sidebar__body">
                <Menu
                    // theme="dark"
                    mode="inline"
                    selectedKeys={[selectedKey]}
                    className="app-sidebar__menu"
                    items={menuItems
                        .map((mi: any) => {
                            if (mi.key === "/admin-review" && !isSuperAdmin) return null;
                            const icon =
                                mi.adminBadge && pendingCount > 0 ? (
                                    collapsed ? (
                                        <Badge count={pendingCount} size="small">
                                            <span style={{ display: "inline-flex" }}>
                                                {mi.icon}
                                            </span>
                                        </Badge>
                                    ) : (
                                        <Badge count={pendingCount} size="small">
                                            {mi.icon}
                                        </Badge>
                                    )
                                ) : (
                                    mi.icon
                                );

                            return {
                                key: mi.key,
                                icon,
                                label: mi.label,
                            };
                        })
                        .filter(Boolean as any)}
                    onClick={({ key }) => handleOnClick(key)}
                />

                <div className="app-sidebar__footer">
                    <Tooltip title="Documentation">
                        <div
                            className="app-sidebar__footer-item"
                            onClick={() => window.open("/documentation", "_blank")}
                        >
                            <ReadOutlined className="app-sidebar__footer-icon" />

                            {!collapsed && <span style={{ fontSize: 14 }}>Documentation</span>}
                        </div>
                    </Tooltip>

                    <div
                        className="app-sidebar__version"
                        onClick={() => window.open("/release-notes", "_blank")}
                    >
                        {collapsed ? (
                            <Tooltip title="Version 1.1">
                                <span>V1.1</span>
                            </Tooltip>
                        ) : (
                            "Version 1.1"
                        )}
                    </div>
                </div>
            </div>
        </Sider>
    );
};

export default Sidebar;
